import { useCallback, useReducer, useEffect, useState, useRef } from "react"

export interface UserPreferencesState {
    columnWidths: Record<string, number>
    columnOrder: string[]
    hiddenColumns: string[]
    fixedColumns: Array<Record<string, "left" | "right">>
}

export type UserPreferencesAction =
    | { type: "SET_COLUMN_WIDTH"; payload: { columnKey: string; width: number } }
    | { type: "SET_COLUMN_ORDER"; payload: string[] }
    | { type: "SET_HIDDEN_COLUMNS"; payload: string[] }
    | { type: "SET_FIXED_COLUMNS"; payload: Array<Record<string, "left" | "right">> }
    | { type: "LOAD_FROM_STORAGE"; payload: UserPreferencesState }
    | { type: "RESET_PREFERENCES" }

const initialState: UserPreferencesState = {
    columnWidths: {},
    columnOrder: [],
    hiddenColumns: [],
    fixedColumns: []
}

const userPreferencesReducer = (state: UserPreferencesState, action: UserPreferencesAction): UserPreferencesState => {
    switch (action.type) {
        case "SET_COLUMN_WIDTH":
            return {
                ...state,
                columnWidths: {
                    ...state.columnWidths,
                    [action.payload.columnKey]: action.payload.width
                }
            }

        case "SET_COLUMN_ORDER":
            return {
                ...state,
                columnOrder: action.payload
            }

        case "SET_HIDDEN_COLUMNS":
            return {
                ...state,
                hiddenColumns: action.payload
            }

        case "SET_FIXED_COLUMNS":
            return {
                ...state,
                fixedColumns: action.payload
            }

        case "LOAD_FROM_STORAGE":
            return action.payload

        case "RESET_PREFERENCES":
            return initialState

        default:
            return state
    }
}

export interface TablePreferencesConfig {
    uniqueStorageKey: string
    enabled: boolean
}

export interface UseUserPreferencesReturn {
    state: UserPreferencesState
    setColumnWidth: (columnKey: string, width: number) => void
    setColumnOrder: (order: string[]) => void
    setHiddenColumns: (columns: string[]) => void
    setFixedColumns: (columns: Array<Record<string, "left" | "right">>) => void
    loadFromStorage: () => void
    saveToStorage: () => void
    resetPreferences: () => void
    isColumnVisible: (columnKey: string) => boolean
}

const DEBOUNCE_TIME = 1_000

export const useUserPreferences = (preferences?: TablePreferencesConfig): UseUserPreferencesReturn => {
    const [state, _dispatch] = useReducer(userPreferencesReducer, initialState)
    const [hasUserChanges, setHasUserChanges] = useState(false)
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const enabled = preferences?.enabled ?? false
    const storageKey = `table_preferences_${preferences?.uniqueStorageKey}`.toLocaleLowerCase()

    const getStorageData = useCallback((): UserPreferencesState | null => {
        if (!enabled) return null

        try {
            const stored = localStorage.getItem(storageKey)
            if (stored) {
                return JSON.parse(stored)
            }
        } catch (error) {
            console.warn("Failed to load user preferences from localStorage:", error)
        }
        return null
    }, [storageKey, enabled])

    const saveToStorage = useCallback(() => {
        if (!enabled) return

        try {
            localStorage.setItem(storageKey, JSON.stringify(state))
        } catch (error) {
            console.warn("Failed to save user preferences to localStorage:", error)
        }
    }, [storageKey, state, enabled])

    const loadFromStorage = useCallback(() => {
        const storedData = getStorageData()
        if (storedData) {
            _dispatch({ type: "LOAD_FROM_STORAGE", payload: storedData })
        }
    }, [getStorageData])

    // Wrapper dispatch that tracks user changes
    const dispatch = useCallback((action: UserPreferencesAction) => {
        setHasUserChanges(true)
        _dispatch(action)
    }, [])

    const setColumnWidth = useCallback(
        (columnKey: string, width: number) => {
            dispatch({ type: "SET_COLUMN_WIDTH", payload: { columnKey, width } })
        },
        [dispatch]
    )

    const setColumnOrder = useCallback(
        (order: string[]) => {
            dispatch({ type: "SET_COLUMN_ORDER", payload: order })
        },
        [dispatch]
    )

    const setHiddenColumns = useCallback(
        (columns: string[]) => {
            dispatch({ type: "SET_HIDDEN_COLUMNS", payload: columns })
        },
        [dispatch]
    )

    const setFixedColumns = useCallback(
        (columns: Array<Record<string, "left" | "right">>) => {
            dispatch({ type: "SET_FIXED_COLUMNS", payload: columns })
        },
        [dispatch]
    )

    const resetPreferences = useCallback(() => {
        if (!enabled) return

        // Reset state and flag first to prevent re-saving
        _dispatch({ type: "RESET_PREFERENCES" })
        setHasUserChanges(false)

        // Then clear localStorage
        setTimeout(() => {
            try {
                localStorage.removeItem(storageKey)
            } catch (error) {
                console.warn("Failed to clear preferences from localStorage:", error)
            }
        }, DEBOUNCE_TIME + 100)
    }, [enabled, storageKey])

    const isColumnVisible = useCallback(
        (columnKey: string): boolean => {
            return !state.hiddenColumns.includes(columnKey)
        },
        [state.hiddenColumns]
    )

    useEffect(() => {
        loadFromStorage()
    }, [loadFromStorage])

    useEffect(() => {
        if (enabled && hasUserChanges) {
            // Clear existing timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
            }

            // Debounce the save operation with DEBOUNCE_TIME delay
            debounceTimeoutRef.current = setTimeout(() => {
                saveToStorage()
                debounceTimeoutRef.current = null
            }, DEBOUNCE_TIME)
        }
    }, [state, enabled, hasUserChanges, saveToStorage])

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
            }
        }
    }, [])

    return {
        state,
        setColumnWidth,
        setColumnOrder,
        setHiddenColumns,
        setFixedColumns,
        loadFromStorage,
        saveToStorage,
        resetPreferences,
        isColumnVisible
    }
}
